import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../store/appStore';
import { useI18n } from '../i18n';
import { PropertyCard } from '../components/property/PropertyCard';
import { PropertyMap } from '../components/property/PropertyMap';
import { MessageForm } from '../components/property/MessageForm';
import { SchedulingForm } from '../components/property/SchedulingForm';
import { ShareButtons } from '../components/property/ShareButtons';
import { ImageLightbox } from '../components/common/ImageLightbox';
import { Property, Auction, Bid } from '../types';
import { placeBidApi, getBidsApi, getProperty } from '../api/client';
import { useToast } from '../components/common/Toast';

export function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { properties, demands, auctions, isAuthenticated, user, placeBid, incrementViews, isFavorite, toggleFavorite } = useAppStore();
  const { t, language } = useI18n();
  const toast = useToast();
  const [selectedImage, setSelectedImage] = useState(0);
  const [showPhone, setShowPhone] = useState(false);
  const [bidAmount, setBidAmount] = useState('');
  const [showBidForm, setShowBidForm] = useState(false);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [showSchedulingForm, setShowSchedulingForm] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isLoadingFavorite, setIsLoadingFavorite] = useState(false);
  const [apiProperty, setApiProperty] = useState<Property | Auction | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [auctionBids, setAuctionBids] = useState<Bid[]>([]);

  const allProperties = [...properties, ...demands, ...auctions];
  const storeProperty = allProperties.find(p => p.id === id);
  // Prefer apiProperty (has owner/company data) over storeProperty (listing cache without owner)
  const property = apiProperty || storeProperty;
  const isAuction = property?.adKind === 'Aukcija';
  const auction = isAuction ? (property as Auction) : null;
  const favoriteStatus = property ? isFavorite(property.id) : false;
  const hasIncrementedViews = useRef(false);

  // Fetch property from API if not found in store OR if store version lacks owner data
  useEffect(() => {
    const fetchProperty = async () => {
      if (!id) return;
      // Skip if store property has owner data already
      if (storeProperty && (storeProperty as any).owner) return;
      
      setIsLoading(!storeProperty); // Only show loading spinner if no store version
      try {
        const data = await getProperty(id);
        if (data) {
          // Transform API response to proper type
          const transformed: Property | Auction = {
            id: String(data.id),
            title: data.title || '',
            description: data.description || '',
            price: Number(data.price) || 0,
            currency: data.currency || 'EUR',
            adKind: data.ad_kind || 'Ponuda',
            adType: data.ad_type || 'Prodaja',
            category: data.category || 'Stan',
            subcategory: data.subcategory || '',
            squareMeters: Number(data.square_meters) || 0,
            rooms: Number(data.rooms) || 0,
            floor: data.floor ? Number(data.floor) : undefined,
            totalFloors: data.total_floors ? Number(data.total_floors) : undefined,
            heating: data.heating || data.heating_type || undefined,
            region: data.region || '',
            city: data.city || '',
            address: data.address || '',
            location: {
              region: data.region || '',
              city: data.city || '',
              address: data.address || '',
              lat: data.lat ? Number(data.lat) : undefined,
              lng: data.lng ? Number(data.lng) : undefined,
            },
            images: typeof data.images === 'string' ? JSON.parse(data.images || '[]') : (data.images || []),
            features: data.features || {},
            views: Number(data.views) || 0,
            contactViews: Number(data.contact_views) || 0,
            status: data.status || 'active',
            approved: Boolean(data.approved),
            featured: Boolean(data.featured),
            createdAt: data.created_at || new Date().toISOString(),
            expiresAt: data.expires_at,
            userId: String(data.user_id || ''),
            contactPhone: data.contact_phone || data.contactPhone || '',
            contactEmail: data.contact_email || data.contactEmail || '',
            // Potražnja-specific fields (ranges)
            priceMin: data.priceMin || (data.price_min ? Number(data.price_min) : undefined),
            priceMax: data.priceMax || (data.price_max ? Number(data.price_max) : undefined),
            squareMetersMin: data.squareMetersMin || (data.square_meters_min ? Number(data.square_meters_min) : undefined),
            squareMetersMax: data.squareMetersMax || (data.square_meters_max ? Number(data.square_meters_max) : undefined),
            roomsMin: data.roomsMin || data.rooms_min || undefined,
            roomsMax: data.roomsMax || data.rooms_max || undefined,
            plotAreaMin: data.plotAreaMin || (data.plot_area_min ? Number(data.plot_area_min) : undefined),
            plotAreaMax: data.plotAreaMax || (data.plot_area_max ? Number(data.plot_area_max) : undefined),
            heatingTypes: data.heatingTypes || (data.heating_types ? (typeof data.heating_types === 'string' ? JSON.parse(data.heating_types) : data.heating_types) : undefined),
            nearbyLocation: data.nearbyLocation || data.nearby_location || undefined,
            plotArea: data.plotArea || (data.plot_area ? Number(data.plot_area) : undefined),
            // Owner/company info
            owner: data.owner || undefined,
            // Auction-specific fields
            ...(data.ad_kind === 'Aukcija' ? {
              currentPrice: Number(data.current_bid) || Number(data.price) || 0,
              startingPrice: Number(data.starting_bid) || Number(data.price) || 0,
              auctionEndsAt: data.auction_end_date || data.expires_at || new Date().toISOString(),
              bids: [],
            } : {}),
          };
          setApiProperty(transformed);
        }
      } catch (error) {
        console.error('Error fetching property:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchProperty();
  }, [id, storeProperty]);

  // Increment views on mount (only once, even in StrictMode) — skip for expired
  useEffect(() => {
    if (id && !hasIncrementedViews.current) {
      // Don't increment views on expired listings
      const propStatus = (apiProperty || storeProperty)?.status;
      const propExpiry = (apiProperty || storeProperty)?.expiresAt;
      const expired = propStatus === 'expired' || (propExpiry && new Date(propExpiry).getTime() < Date.now());
      if (!expired) {
        hasIncrementedViews.current = true;
        incrementViews(id);
      }
    }
  }, [id, incrementViews, apiProperty, storeProperty]);

  // Fetch bids for auction properties
  useEffect(() => {
    const fetchBids = async () => {
      if (!id || !isAuction) return;
      
      try {
        const result = await getBidsApi(id);
        if (result.success && result.data) {
          const formattedBids: Bid[] = result.data.map((b: any) => ({
            id: String(b.id),
            auctionId: id,
            bidderId: String(b.user_id),
            bidderName: b.user_name || 'Korisnik',
            amount: Number(b.amount),
            comment: b.comment,
            status: b.status || 'pending',
            ownerResponse: b.owner_response,
            createdAt: b.created_at
          }));
          setAuctionBids(formattedBids);
        }
      } catch (error) {
        console.error('Error fetching bids:', error);
      }
    };
    
    fetchBids();
  }, [id, isAuction]);

  // Check if user can see phone
  const canSeePhone = isAuthenticated;
  // Check if user can send messages/offers
  const canSendMessages = isAuthenticated && (user?.emailVerified || user?.verified);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">{t.property.notFound}</h1>
          <Link to="/ponuda" className="text-amber-600 hover:text-amber-500">{t.property.backToListings}</Link>
        </div>
      </div>
    );
  }

  const similarProperties = properties
    .filter(p => p.id !== property.id && p.category === property.category)
    .slice(0, 3);

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('sr-RS').format(price) + ' ' + currency;
  };
  
  const handleToggleFavorite = async () => {
    if (!isAuthenticated) {
      navigate('/prijava');
      return;
    }
    
    if (!property) return;
    
    setIsLoadingFavorite(true);
    try {
      await toggleFavorite(property.id);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsLoadingFavorite(false);
    }
  };

  const handleBid = async () => {
    if (!isAuthenticated) {
      navigate('/prijava');
      return;
    }
    
    if (!bidAmount) {
      toast.warning(
        language === 'sr' ? 'Nedostaje ponuda' : 'Missing bid',
        language === 'sr' ? 'Unesite iznos ponude' : 'Enter bid amount'
      );
      return;
    }
    
    if (!property) {
      toast.error(
        language === 'sr' ? 'Greška' : 'Error',
        language === 'sr' ? 'Oglas nije pronađen' : 'Listing not found'
      );
      return;
    }
    
    // Get current price - use auction fields if available, otherwise use property price
    const currentPrice = auction?.currentPrice || Number(property.price) || 0;
    const propertyId = property.id;
    
    const amount = Number(bidAmount);
    if (amount <= currentPrice) {
      toast.warning(
        language === 'sr' ? 'Premala ponuda' : 'Bid too low',
        language === 'sr' ? 'Ponuda mora biti veća od trenutne cene' : 'Bid must be higher than current price'
      );
      return;
    }

    try {
      const result = await placeBidApi(propertyId, amount);
      
      if (result.success) {
        // Update local state with new bid
        const newBid: Bid = {
          id: String(result.data?.id || Date.now()),
          auctionId: propertyId,
          userId: user!.id,
          userName: result.data?.user_name || user!.name,
          amount,
          status: 'pending',
          createdAt: result.data?.created_at || new Date().toISOString(),
        };
        
        if (auction) {
          placeBid(auction.id, newBid);
        }
        setBidAmount('');
        setShowBidForm(false);
        toast.success(
          language === 'sr' ? 'Ponuda poslata' : 'Bid placed',
          result.message || (language === 'sr' ? 'Vaša ponuda je uspešno poslata!' : 'Your bid has been placed successfully!')
        );
      } else {
        toast.error(
          language === 'sr' ? 'Greška' : 'Error',
          result.error || (language === 'sr' ? 'Greška pri slanju ponude' : 'Error placing bid')
        );
      }
    } catch (error) {
      console.error('Error placing bid:', error);
      toast.error(
        language === 'sr' ? 'Greška' : 'Error',
        language === 'sr' ? 'Greška pri slanju ponude. Pokušajte ponovo.' : 'Error placing bid. Please try again.'
      );
    }
  };

  const featureLabels: Record<string, Record<string, string>> = {
    odmahUseljiv: { sr: 'Odmah useljiv', en: 'Move-in ready' },
    uknjizen: { sr: 'Uknjižen', en: 'Registered' },
    struja: { sr: 'Struja', en: 'Electricity' },
    voda: { sr: 'Voda', en: 'Water' },
    klima: { sr: 'Klima', en: 'Air conditioning' },
    interfon: { sr: 'Interfon', en: 'Intercom' },
    zaNepusace: { sr: 'Za nepušače', en: 'Non-smoking' },
    dozvoljenKucniLjubimci: { sr: 'Dozvoljeni kućni ljubimci', en: 'Pets allowed' },
    lift: { sr: 'Lift', en: 'Elevator' },
    terasa: { sr: 'Terasa', en: 'Terrace' },
    internet: { sr: 'Internet', en: 'Internet' },
    garaza: { sr: 'Garaža', en: 'Garage' },
    parkingMesto: { sr: 'Parking mesto', en: 'Parking' },
    videoNadzor: { sr: 'Video nadzor', en: 'Video surveillance' },
    kanalizacija: { sr: 'Kanalizacija', en: 'Sewage' },
    putDoObjekta: { sr: 'Put do objekta', en: 'Road access' },
    podHipotekom: { sr: 'Pod hipotekom', en: 'Under mortgage' },
    mozeZamena: { sr: 'Moguća zamena', en: 'Exchange possible' },
    hitno: { sr: 'Hitno', en: 'Urgent' },
    dozvoljenaGradnja: { sr: 'Dozvoljena gradnja', en: 'Building permit' },
  };

  // Determine if listing is expired (either by status or by time)
  const isExpired = property.status === 'expired' || 
    (property.expiresAt && new Date(property.expiresAt).getTime() < Date.now());

  // Heating type labels
  const heatingLabels: Record<string, Record<string, string>> = {
    CG: { sr: 'Centralno grejanje', en: 'Central heating' },
    EG: { sr: 'Etažno grejanje', en: 'Floor heating' },
    TA: { sr: 'TA peć', en: 'Storage heater' },
    Gas: { sr: 'Gas', en: 'Gas' },
    Drvo: { sr: 'Drvo', en: 'Wood' },
    'Bez grejanja': { sr: 'Bez grejanja', en: 'No heating' },
  };

  const activeFeatures = Object.entries(property.features)
    .filter(([, value]) => value)
    .map(([key]) => featureLabels[key]?.[language] || key);

  // Get heating label
  const heatingLabel = property.heating ? heatingLabels[property.heating]?.[language] || property.heating : null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm">
          <ol className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
            <li><Link to="/" className="hover:text-amber-600">{t.nav.home}</Link></li>
            <li>/</li>
            <li>
              <Link 
                to={isAuction ? '/aukcije' : property.adKind === 'Potražnja' ? '/potraznja' : '/ponuda'} 
                className="hover:text-amber-600"
              >
                {isAuction ? t.nav.auctions : property.adKind === 'Potražnja' ? t.nav.demands : t.nav.offers}
              </Link>
            </li>
            <li>/</li>
            <li className="text-slate-900 dark:text-white font-medium truncate max-w-xs">{property.title}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Expired listing banner */}
          {isExpired && (
            <div className="lg:col-span-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-xl p-4 flex items-center gap-3">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <p className="font-semibold text-red-800 dark:text-red-300">
                  {language === 'sr' ? 'Ovaj oglas je istekao' : 'This listing has expired'}
                </p>
                <p className="text-sm text-red-700 dark:text-red-400">
                  {language === 'sr' 
                    ? 'Oglas više nije aktivan. Vidljiv je samo vama kao vlasniku ili administratoru.'
                    : 'This listing is no longer active. It is only visible to you as the owner or administrator.'}
                </p>
              </div>
            </div>
          )}
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Image Gallery */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden mb-6"
            >
              {property.images.length > 0 ? (
                <>
                  <div className="relative h-[400px]">
                    {property.images[selectedImage]?.startsWith('data:video/') ? (
                      <video
                        src={property.images[selectedImage]}
                        controls
                        className="w-full h-full object-contain bg-black"
                      />
                    ) : (
                      <img
                        src={property.images[selectedImage]}
                        alt={property.title}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => setLightboxOpen(true)}
                      />
                    )}
                    {isAuction && (
                      <div className="absolute top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg font-medium">
                        Aukcija
                      </div>
                    )}

                    {/* Fullscreen icon */}
                    <button
                      onClick={() => setLightboxOpen(true)}
                      className={`absolute top-4 ${isAuction ? 'right-28' : 'right-4'} w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-lg flex items-center justify-center transition-colors`}
                      aria-label="Otvori galeriju"
                      title="Otvori galeriju"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                      </svg>
                    </button>

                    {/* Navigation Arrows */}
                    {property.images.length > 1 && (
                      <>
                        {/* Previous Arrow */}
                        <button
                          onClick={() => setSelectedImage((prev) => (prev === 0 ? property.images.length - 1 : prev - 1))}
                          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
                          aria-label="Prethodna slika"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>

                        {/* Next Arrow */}
                        <button
                          onClick={() => setSelectedImage((prev) => (prev === property.images.length - 1 ? 0 : prev + 1))}
                          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
                          aria-label="Sledeća slika"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>

                        {/* Image Counter */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                          {selectedImage + 1} / {property.images.length}
                        </div>
                      </>
                    )}
                  </div>
                  {property.images.length > 1 && (
                    <div className="flex gap-2 p-4 overflow-x-auto">
                      {property.images.map((img, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImage(index)}
                          className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                            selectedImage === index ? 'border-amber-500' : 'border-transparent'
                          }`}
                        >
                          {img.startsWith('data:video/') ? (
                            <div className="w-full h-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                              <svg className="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                          ) : (
                            <img src={img} alt="" className="w-full h-full object-cover" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="h-[400px] bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                  <svg className="w-16 h-16 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </motion.div>

            {/* Image Lightbox */}
            {property.images.length > 0 && (
              <ImageLightbox
                images={property.images}
                initialIndex={selectedImage}
                isOpen={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
              />
            )}

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 mb-6"
            >
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                  property.adType === 'Prodaja' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                }`}>
                  {/* Za Potražnju prikaži Kupovina/Rentiranje umesto Prodaja/Izdavanje */}
                  {property.adKind === 'Potražnja' 
                    ? (property.adType === 'Prodaja' 
                        ? (language === 'sr' ? 'Kupovina' : 'Buying') 
                        : (language === 'sr' ? 'Rentiranje' : 'Renting'))
                    : property.adType
                  }
                </span>
                <span className="px-3 py-1 text-sm font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                  {property.category}
                </span>
                {/* Favorite button */}
                {isAuthenticated && (
                  <button
                    onClick={handleToggleFavorite}
                    disabled={isLoadingFavorite}
                    className="ml-auto flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                  >
                    {favoriteStatus ? (
                      <>
                        <svg className="w-5 h-5 text-red-500 fill-current" viewBox="0 0 24 24">
                          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                        </svg>
                        <span className="text-sm font-medium text-slate-900 dark:text-white">
                          {language === 'sr' ? 'Omiljeno' : 'Saved'}
                        </span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                        <span className="text-sm font-medium text-slate-900 dark:text-white">
                          {language === 'sr' ? 'Sačuvaj' : 'Save'}
                        </span>
                      </>
                    )}
                  </button>
                )}
                {property.subcategory && (
                  <span className="px-3 py-1 text-sm font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                    {property.subcategory}
                  </span>
                )}
                {/* Share Button */}
                <ShareButtons 
                  url={`/oglas/${property.id}`} 
                  title={property.title} 
                  description={property.description}
                />
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-4">{property.title}</h1>

              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 mb-6">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                {property.location.address && `${property.location.address}, `}
                {property.location.city}, {property.location.region}
              </div>

              {/* Key Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {/* Površina - za Potražnja prikazujemo raspon */}
                {property.adKind === 'Potražnja' && (property.squareMetersMin || property.squareMetersMax) ? (
                  <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900 dark:text-white">
                      {property.squareMetersMin || '0'} - {property.squareMetersMax || '∞'}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">m²</div>
                  </div>
                ) : property.squareMeters > 0 && (
                  <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900 dark:text-white">{property.squareMeters}</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">m²</div>
                  </div>
                )}
                {/* Sobe - za Potražnja prikazujemo raspon */}
                {property.adKind === 'Potražnja' && (property.roomsMin || property.roomsMax) ? (
                  <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900 dark:text-white">
                      {property.roomsMin || '0'} - {property.roomsMax || '∞'}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">{t.property.rooms}</div>
                  </div>
                ) : Number(property.rooms) > 0 ? (
                  <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900 dark:text-white">{property.rooms}</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">{t.property.rooms}</div>
                  </div>
                ) : null}
                {property.floor !== undefined && property.floor !== null && Number(property.floor) >= 0 && (
                  <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900 dark:text-white">{property.floor}/{property.totalFloors || '?'}</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">{t.property.floor}</div>
                  </div>
                )}
                {/* Površina placa - za Potražnja prikazujemo raspon */}
                {property.adKind === 'Potražnja' && (property.plotAreaMin || property.plotAreaMax) ? (
                  <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900 dark:text-white">
                      {property.plotAreaMin || '0'} - {property.plotAreaMax || '∞'}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">{t.property.plotArea} m²</div>
                  </div>
                ) : Number(property.plotArea) > 0 ? (
                  <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900 dark:text-white">{property.plotArea}</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">{t.property.plotArea} m²</div>
                  </div>
                ) : null}
                {/* Tipovi grejanja za Potražnja */}
                {property.adKind === 'Potražnja' && property.heatingTypes && property.heatingTypes.length > 0 && (
                  <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4 text-center">
                    <div className="text-lg font-bold text-slate-900 dark:text-white">
                      {property.heatingTypes.join(', ')}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">Grejanje</div>
                  </div>
                )}
              </div>

              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">{t.property.description}</h2>
              <p className="text-slate-600 dark:text-slate-400 whitespace-pre-wrap mb-6">{property.description}</p>

              {(activeFeatures.length > 0 || heatingLabel) && (
                <>
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">{t.property.features}</h2>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {/* Heating type - show first with different color */}
                    {heatingLabel && (
                      <span
                        className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded-full text-sm flex items-center gap-1"
                      >
                        🔥 {language === 'sr' ? 'Grejanje:' : 'Heating:'} {heatingLabel}
                      </span>
                    )}
                    {activeFeatures.map((feature) => (
                      <span
                        key={feature}
                        className="px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400 rounded-full text-sm"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </>
              )}

              {/* Map */}
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">{t.property.location}</h2>
              <PropertyMap property={property} height="300px" />
            </motion.div>

            {/* Auction Bids */}
            {isAuction && auction && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 mb-6"
              >
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">{t.property.bidHistory}</h2>
                {auctionBids.length === 0 ? (
                  <p className="text-slate-500 dark:text-slate-400">{t.property.noMoreBids} {t.property.beFirst}</p>
                ) : (
                  <div className="space-y-3">
                    {[...auctionBids].reverse().map((bid, index) => (
                      <div key={bid.id} className={`flex items-center justify-between p-3 rounded-lg ${
                        index === 0 ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800' : 'bg-slate-50 dark:bg-slate-700'
                      }`}>
                        <div>
                          <span className="font-medium text-slate-900 dark:text-white">{bid.bidderName}</span>
                          <span className="text-sm text-slate-500 dark:text-slate-400 ml-2">
                            {new Date(bid.createdAt).toLocaleDateString('sr-RS')}
                          </span>
                          {bid.status === 'approved' && (
                            <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                              {language === 'sr' ? 'Prihvaćeno' : 'Accepted'}
                            </span>
                          )}
                          {bid.status === 'rejected' && (
                            <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded">
                              {language === 'sr' ? 'Odbijeno' : 'Rejected'}
                            </span>
                          )}
                        </div>
                        <div className="font-semibold text-amber-600">
                          {formatPrice(bid.amount, 'EUR')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 sticky top-24"
            >
              {/* Price */}
              <div className="mb-6">
                {isAuction ? (
                  <>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Trenutna cena:</p>
                    <p className="text-3xl font-bold text-amber-600">
                      {formatPrice(auction!.currentPrice, property.currency)}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                      {t.property.startingPrice}: {formatPrice(auction!.startingPrice, property.currency)}
                    </p>
                    {auctionBids.length > 0 && (
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {auctionBids.length} {t.property.bidsCount}
                      </p>
                    )}

                    {/* Bid Form - only for active listings */}
                    {!isExpired && (
                      showBidForm ? (
                      <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {t.property.yourBid}
                        </label>
                        <input
                          type="number"
                          value={bidAmount}
                          onChange={(e) => setBidAmount(e.target.value)}
                          min={auction!.currentPrice + 100}
                          placeholder={`${t.property.minBid} ${auction!.currentPrice + 100}`}
                          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white mb-2"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleBid}
                            className="flex-1 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400"
                          >
                            {t.property.submitBid}
                          </button>
                          <button
                            onClick={() => setShowBidForm(false)}
                            className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-600"
                          >
                            {t.property.cancel}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowBidForm(true)}
                        className="w-full mt-4 py-3 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors"
                      >
                        {t.property.placeBid}
                      </button>
                    )
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                      {property.adKind === 'Potražnja' ? 'Budžet:' : 'Cena:'}
                    </p>
                    <p className="text-3xl font-bold text-amber-600">
                      {property.adKind === 'Potražnja' && (property.priceMin || property.priceMax) ? (
                        // Za Potražnja prikazujemo raspon cena
                        <>
                          {property.priceMin ? formatPrice(property.priceMin, property.currency) : '0'}
                          {' - '}
                          {property.priceMax ? formatPrice(property.priceMax, property.currency) : '∞'}
                        </>
                      ) : (
                        // Za Ponuda prikazujemo običnu cenu
                        <>
                          {formatPrice(property.price, property.currency)}
                          {property.adType === 'Izdavanje' && <span className="text-lg font-normal">{t.property.perMonth}</span>}
                        </>
                      )}
                    </p>
                  </>
                )}
              </div>

              {/* Contact - hidden for expired listings */}
              {!isExpired && (
              <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                <h3 className="font-semibold text-slate-900 dark:text-white mb-4">{t.property.contact}</h3>
                
                {/* Phone - only for logged in users */}
                {canSeePhone ? (
                  showPhone ? (
                    <a
                      href={`tel:${property.contactPhone}`}
                      className="flex items-center justify-center gap-2 w-full bg-amber-500 text-slate-900 font-semibold py-3 rounded-lg hover:bg-amber-400 transition-colors mb-3"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {property.contactPhone}
                    </a>
                  ) : (
                    <button
                      onClick={() => setShowPhone(true)}
                      className="flex items-center justify-center gap-2 w-full bg-amber-500 text-slate-900 font-semibold py-3 rounded-lg hover:bg-amber-400 transition-colors mb-3"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {t.property.showPhone}
                    </button>
                  )
                ) : (
                  <button
                    onClick={() => navigate('/prijava')}
                    className="flex items-center justify-center gap-2 w-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 font-semibold py-3 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors mb-3"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    {language === 'sr' ? 'Prijavite se za telefon' : 'Login for phone'}
                  </button>
                )}
                
                {property.contactEmail && isAuthenticated ? (
                  <a
                    href={`mailto:${property.contactEmail}`}
                    className="flex items-center justify-center gap-2 w-full border border-amber-500 text-amber-600 font-semibold py-3 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {t.property.sendEmail}
                  </a>
                ) : property.contactEmail && !isAuthenticated ? (
                  <button
                    onClick={() => navigate('/prijava')}
                    className="flex items-center justify-center gap-2 w-full border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 font-semibold py-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    {language === 'sr' ? 'Prijavite se za email' : 'Login for email'}
                  </button>
                ) : null}
                
                {/* Company Profile Link - for legal entities */}
                {(property as any).owner?.userType === 'pravno_lice' && (
                  <div className="mt-3 space-y-2">
                    <Link
                      to={`/kompanija/${(property as any).owner?.company?.username || (property as any).owner?.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                      className="flex items-center justify-center gap-2 w-full bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400 font-semibold py-3 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      {language === 'sr' ? 'Svi oglasi oglašivača' : 'All listings from this advertiser'}
                    </Link>
                    {/* Company rating */}
                    {(property as any).owner?.company?.reviewStats?.total > 0 && (
                      <Link
                        to={`/kompanija/${(property as any).owner?.company?.username || (property as any).owner?.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}#reviews`}
                        className="flex items-center justify-center gap-2 w-full text-sm text-slate-600 dark:text-slate-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors py-1"
                      >
                        <svg className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="font-semibold text-amber-600">{(property as any).owner.company.reviewStats.average}/10</span>
                        <span>({(property as any).owner.company.reviewStats.total} {language === 'sr' ? 'ocena' : 'reviews'})</span>
                      </Link>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Stats */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-6 mt-6 text-sm text-slate-500 dark:text-slate-400">
                {Number(property.views) > 0 && (
                  <div className="flex justify-between mb-2">
                    <span>{t.property.views}:</span>
                    <span className="font-medium text-slate-900 dark:text-white">{property.views}</span>
                  </div>
                )}
                <div className="flex justify-between mb-2">
                  <span>{t.property.published}:</span>
                  <span className="font-medium text-slate-900 dark:text-white">
                    {new Date(property.createdAt).toLocaleDateString(language === 'sr' ? 'sr-RS' : 'en-US')}
                  </span>
                </div>
                {/* Price per m2 */}
                {property.squareMeters > 0 && (
                  <div className="flex justify-between">
                    <span>{t.property.pricePerM2}:</span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {Math.round(property.price / property.squareMeters)} {property.currency}/m²
                    </span>
                  </div>
                )}
              </div>

              {/* Message & Schedule Buttons - hidden for expired listings */}
              {/* Za ponude - pošalji poruku i zakaži posetu */}
              {!isExpired && !isAuction && property.adKind !== 'Potražnja' && (
                <div className="border-t border-slate-200 dark:border-slate-700 pt-6 mt-6 space-y-3">
                  <button
                    onClick={() => {
                      if (!isAuthenticated) {
                        navigate('/prijava');
                        return;
                      }
                      setShowMessageForm(true);
                    }}
                    className="flex items-center justify-center gap-2 w-full border border-emerald-500 text-emerald-600 font-semibold py-3 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                    {t.property.sendMessage}
                  </button>
                  <button
                    onClick={() => {
                      if (!isAuthenticated) {
                        navigate('/prijava');
                        return;
                      }
                      setShowSchedulingForm(true);
                    }}
                    className="flex items-center justify-center gap-2 w-full border border-blue-500 text-blue-600 font-semibold py-3 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {t.property.scheduleVisit}
                  </button>
                </div>
              )}

              {/* Za potražnje - samo pošalji poruku (nema zakazivanja posete) */}
              {property.adKind === 'Potražnja' && (
                <div className="border-t border-slate-200 dark:border-slate-700 pt-6 mt-6 space-y-3">
                  <button
                    onClick={() => {
                      if (!isAuthenticated) {
                        navigate('/prijava');
                        return;
                      }
                      setShowMessageForm(true);
                    }}
                    className="flex items-center justify-center gap-2 w-full border border-emerald-500 text-emerald-600 font-semibold py-3 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                    {language === 'sr' ? 'Kontaktiraj korisnika' : 'Contact User'}
                  </button>
                </div>
              )}

              {/* Za aukcije - samo pošalji poruku */}
              {isAuction && (
                <div className="border-t border-slate-200 dark:border-slate-700 pt-6 mt-6 space-y-3">
                  <button
                    onClick={() => {
                      if (!isAuthenticated) {
                        navigate('/prijava');
                        return;
                      }
                      setShowMessageForm(true);
                    }}
                    className="flex items-center justify-center gap-2 w-full border border-emerald-500 text-emerald-600 font-semibold py-3 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                    {t.property.sendMessage}
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        </div>

        {/* Similar Properties */}
        {similarProperties.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
              {t.property.similarListings}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {similarProperties.map((p) => (
                <PropertyCard key={p.id} property={p} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Message Form Modal */}
      <AnimatePresence>
        {showMessageForm && (
          <MessageForm
            property={property}
            onClose={() => setShowMessageForm(false)}
          />
        )}
      </AnimatePresence>

      {/* Scheduling Form Modal */}
      <AnimatePresence>
        {showSchedulingForm && (
          <SchedulingForm
            property={property}
            onClose={() => setShowSchedulingForm(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
